import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Store, ShoppingBag, ArrowRight } from 'lucide-react';
import BrandLogo from '../components/brand/BrandLogo';

/**
 * FABTEXS AI role-selection gate (Step 1). Shown ONLY on first app launch
 * (reached from /start when there is no existing seller session, buyer
 * identity, or previously-chosen role). Role selection is fully
 * deterministic — the AI/Sales Brain is never involved.
 *
 *   • "I AM A SELLER" → persist role + enter the EXISTING seller login flow.
 *   • "I AM A BUYER"  → persist role + open the Buyer Home (/me).
 *
 * The chosen role is stored in localStorage under `fabtexs.role`, so /start
 * routes returning users straight to the right experience and never shows
 * this screen again.
 */
export default function RoleSelect() {
  const nav = useNavigate();

  const choose = (role) => {
    try { localStorage.setItem('fabtexs.role', role); } catch (_e) { /* localStorage disabled */ }
    nav(role === 'seller' ? '/login' : '/me', { replace: true });
  };

  return (
    <div className="min-h-screen mesh-bg flex flex-col items-center justify-center px-6 py-12" data-testid="role-select-screen">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <BrandLogo height={40} />
        </div>

        <div className="text-center mb-9">
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-fab-text tracking-tight">
            Welcome to FABTEXS AI
          </h1>
          <p className="mt-3 text-[15px] text-fab-text-secondary">
            How are you using FABTEXS AI?
          </p>
        </div>

        <div className="space-y-4">
          <button
            type="button"
            data-testid="role-select-buyer-btn"
            onClick={() => choose('buyer')}
            className="group w-full flex items-center gap-4 bg-white rounded-2xl p-5 border border-fab-primary-lighter shadow-sm hover:shadow-md hover:border-fab-primary transition-[box-shadow,border-color] text-left"
          >
            <div className="w-12 h-12 rounded-xl bg-fab-primary-lighter flex items-center justify-center shrink-0">
              <ShoppingBag className="w-6 h-6 text-fab-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-[17px] font-bold text-fab-text">I am a Buyer</div>
              <div className="text-[13px] text-fab-text-secondary mt-0.5">Browse catalogues and chat with your sellers.</div>
            </div>
            <ArrowRight className="w-5 h-5 text-fab-text-muted group-hover:text-fab-primary group-hover:translate-x-0.5 transition-transform shrink-0" />
          </button>

          <button
            type="button"
            data-testid="role-select-seller-btn"
            onClick={() => choose('seller')}
            className="group w-full flex items-center gap-4 bg-white rounded-2xl p-5 border border-fab-primary-lighter shadow-sm hover:shadow-md hover:border-fab-primary transition-[box-shadow,border-color] text-left"
          >
            <div className="w-12 h-12 rounded-xl bg-fab-wa-light flex items-center justify-center shrink-0">
              <Store className="w-6 h-6 text-fab-wa-dark" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-[17px] font-bold text-fab-text">I am a Seller</div>
              <div className="text-[13px] text-fab-text-secondary mt-0.5">Manage your catalogue, orders and AI assistant.</div>
            </div>
            <ArrowRight className="w-5 h-5 text-fab-text-muted group-hover:text-fab-primary group-hover:translate-x-0.5 transition-transform shrink-0" />
          </button>
        </div>

        <p className="text-center text-[11.5px] text-fab-text-muted mt-8">
          You can always come back — we&apos;ll remember your choice on this device.
        </p>
        <p className="text-center text-[12px] mt-3">
          <Link to="/home" className="font-semibold text-fab-text-secondary hover:text-fab-primary" data-testid="role-select-learn-more">
            Learn more about FABTEXS AI
          </Link>
        </p>
      </div>
    </div>
  );
}
